/**
 * Sidebar Navigation — Warm charcoal design system.
 * Desktop: 232px fixed | Tablet: 200px | Mobile: collapsed to icon-only 48px
 */
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { usePlanFeatures } from '@/hooks/usePlanFeatures';
import { useClinicModules } from '@/hooks/useClinicModules';
import { buildNavGroups, type NavIcon } from './navModel';
import { PatientTypeahead } from './PatientTypeahead';
import { useState } from 'react';

// SVG icons (16×16, stroke-only)
const Icons = {
  dashboard: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  patients: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  assessments: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  bps: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  exercises: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="12" width="4" height="8" rx="1"/><rect x="10" y="8" width="4" height="12" rx="1"/><rect x="17" y="4" width="4" height="16" rx="1"/></svg>,
  conditions: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
  protocols: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  reports: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  insights: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  settings: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  frontdesk: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/><path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/><path d="M8 18h.01"/><path d="M12 18h.01"/></svg>,
  kpi: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 3v18h18"/><path d="M7 15l4-5 3 3 5-7"/></svg>,
  search: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
};

interface NavGroup { label: string; items: { to: string; icon: JSX.Element; label: string; badge?: string; dot?: boolean }[] }

// QA UX FIX-16: admin group (Audit Log + GDPR) — only rendered for clinic_admin.
const Icons2 = {
  audit: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M9 13h6"/><path d="M9 17h4"/></svg>,
  shield: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
};

export function Sidebar(): JSX.Element {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { isCheckerOnly, hasFullClinical, hasAnalytics, hasExerciseLibrary, plan } = usePlanFeatures();
  const [collapsed, setCollapsed] = useState(false);

  const { frontDesk } = useClinicModules();

  // Q-4/Q-5 — the item set is a pure function of role, plan and modules
  // (navModel.ts), pinned in __tests__/navModel.test.ts. The icons stay here
  // because they are JSX; the model names them.
  const ICON_BY_KEY: Record<NavIcon, JSX.Element> = {
    dashboard: Icons.dashboard, patients: Icons.patients, frontdesk: Icons.frontdesk,
    insights: Icons.insights, exercises: Icons.exercises, conditions: Icons.conditions,
    settings: Icons.settings, protocols: Icons.protocols, audit: Icons2.audit, shield: Icons2.shield,
    kpi: Icons.kpi,
  };
  const groups: NavGroup[] = buildNavGroups({
    role: user?.role,
    features: { isCheckerOnly, hasFullClinical, hasAnalytics, hasExerciseLibrary },
    modules: { frontDesk },
    // K1-2 — the clinic-scoped owner flag from GET /api/auth/me. A UI hint
    // only: the server re-reads it on every /api/kpi request.
    isClinicOwner: user?.isClinicOwner === true,
    staffBoardVisible: user?.staffBoardVisible === true,
  }).map((g) => ({
    label: g.label,
    items: g.items.map((i) => ({ to: i.to, icon: ICON_BY_KEY[i.icon], label: t(i.labelKey, i.defaultLabel) })),
  }));

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const displayName = user ? `${user.title || ''} ${user.firstName} ${user.lastName}`.trim() : 'Clinician';
  const initials = user ? `${(user.firstName || '?')[0]}${(user.lastName || '?')[0]}`.toUpperCase() : '?';
  const role = user?.role === 'clinic_admin' ? 'Clinic admin' : user?.role === 'clinician' ? 'Clinician' : user?.role === 'front_desk' ? 'Reception' : user?.role || '';

  return (
    <aside className={`flex-shrink-0 flex flex-col bg-surface-sidebar border-r border-charcoal-200 transition-all duration-200 ${collapsed ? 'w-[48px]' : 'w-[248px]'} md:w-[220px] lg:w-[248px] h-screen sticky top-0`}>
      {/* Logo — Stage A.1 (2026-05-07): wrap in <Link> so the
          brand mark navigates to dashboard. Hover style matches the
          existing nav-item hover (`hover:bg-surface-active`) so the
          interaction reads as standard for the sidebar. */}
      <Link
        to="/dashboard"
        aria-label="Go to dashboard"
        className="px-4 py-4 flex items-center gap-2.5 hover:bg-surface-active transition-colors no-underline"
      >
        <div className="w-7 h-7 rounded-md bg-charcoal-900 flex items-center justify-center flex-shrink-0">
          <span className="text-[9px] font-medium text-charcoal-50">MI</span>
        </div>
        {!collapsed && (
          <div>
            <span className="text-[15px] font-medium text-charcoal-900">MSK Insight</span>
            <span className="ml-1.5 text-[9px] px-1 py-0.5 bg-charcoal-100 text-charcoal-500 rounded">v2</span>
          </div>
        )}
      </Link>

      {/* Search — Run T / T-1. This was a <div> whose only behaviour was
          `onClick={() => navigate('/patients')}`: it could not be typed into,
          it had no dropdown, and the ⌘K hint beside it had no handler behind
          it anywhere in the app. It is a real typeahead now. */}
      {!collapsed && <PatientTypeahead />}

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto px-2.5">
        {groups.map((group) => (
          <div key={group.label} className="mb-4">
            {!collapsed && (
              <div className="px-2.5 mb-1.5 text-[10.5px] font-medium uppercase text-charcoal-400 tracking-[0.7px]">
                {group.label}
              </div>
            )}
            {group.items.map((item) => {
              const active = isActive(item.to);
              return (
                <Link key={item.to} to={item.to}
                  className={`flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[14px] mb-0.5 transition-all min-h-[40px] ${
                    active
                      ? 'bg-surface-card text-charcoal-900 font-medium shadow-sm'
                      : 'text-charcoal-600 hover:bg-surface-hover'
                  }`}>
                  <span className={active ? 'opacity-70' : 'opacity-40'}>{item.icon}</span>
                  {!collapsed && <span>{item.label}</span>}
                  {!collapsed && item.badge && (
                    <span className="ml-auto text-[9px] px-1.5 py-0.5 bg-charcoal-100 text-charcoal-500 rounded-full font-medium">{item.badge}</span>
                  )}
                  {!collapsed && item.dot && (
                    <span className="ml-auto w-2 h-2 rounded-full bg-green-500" />
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Plan badge / Upgrade prompt */}
      {!collapsed && isCheckerOnly && (
        <div className="mx-3 mb-2 p-3 rounded-lg bg-indigo-50 border border-indigo-200">
          <p className="text-[11px] font-medium text-indigo-700 mb-1.5">Upgrade to MSK Insight</p>
          <p className="text-[10px] text-indigo-600 mb-2">Get patients, assessments, DDx, AI reports, and more.</p>
          <Link to="/settings/billing" className="block text-center text-[11px] font-medium text-white bg-indigo-600 rounded-md py-1.5 hover:bg-indigo-700 transition-colors">
            Upgrade Now →
          </Link>
        </div>
      )}
      {!collapsed && !isCheckerOnly && plan !== 'trialing' && (
        <div className="mx-3 mb-2 px-3 py-1.5 rounded-md bg-green-50 border border-green-200 text-center">
          <span className="text-[10px] text-green-700 font-medium">✓ Symptom Checker Included</span>
        </div>
      )}

      {/* User block */}
      <div className="px-3 py-3 border-t border-charcoal-200 flex items-center gap-2.5">
        <div className="w-[30px] h-[30px] rounded-full bg-charcoal-900 flex items-center justify-center text-[10px] text-charcoal-50 font-medium flex-shrink-0">
          {initials}
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-charcoal-900 truncate">{displayName}</div>
            <div className="text-[11px] text-charcoal-500">{role}</div>
          </div>
        )}
      </div>
    </aside>
  );
}
